import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  entitiesProvider,
  IEntities,
} from '../../../data/providers/entitiesProvider';
import { ISelectedEntitySettings } from '../../../data/providers/settings/selectedEntitySettingsProvider';
import { userProvider } from '../../../data/providers/userProvider';
import {
  EntityType,
  IAthlete,
  IGroup,
  ITeam,
  IUser,
} from '../../../data/types';
import { setSelectedEntitySettings } from '../../../data/writers/settings/selectedEntitySettings';
import { Column } from '../../layout/Column';
import { MainGrid } from '../../layout/MainGrid';
import { Row } from '../../layout/Row';
import { EntityView } from '../../sections/entity/EntityView';
import { Card } from '../../shared/card/Card';
import { EntitySelector } from './EntitySelector';

interface IMatchParams {
  entityType: string;
  entityId: string;
}

export const Dashboard = withRouter(
  (props: RouteComponentProps<IMatchParams>): React.ReactElement => {
    const [athletes, setAthletes] = React.useState<IAthlete[]>([]);
    const [groups, setGroups] = React.useState<IGroup[]>([]);
    const [isAdmin, setAdmin] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [selectedAthlete, setSelectedAthlete] = React.useState<IAthlete>();
    const [selectedGroup, setSelectedGroup] = React.useState<IGroup>();
    const [selectedTeam, setSelectedTeam] = React.useState<ITeam>();
    const [settings, setSettings] = React.useState<ISelectedEntitySettings>();
    const [teams, setTeams] = React.useState<ITeam[]>([]);

    /**
     * @description subscribes to the current logged in user
     */
    React.useEffect((): (() => void) => {
      const userSubscriber = userProvider().subscribe((user: IUser): void => {
        if (isEmpty(user)) return;
        setAdmin(!!user.claims?.orgAdmin);
      });
      return (): void => {
        userSubscriber?.unsubscribe();
      };
    }, []);

    /**
     * @description subscribes to teams, groups, and athletes
     */
    React.useEffect((): (() => void) => {
      const entitiesSubscriber = entitiesProvider().subscribe(
        (entities: IEntities): void => {
          setSettings(entities.settings);
          setAthletes(entities.athletes);
          setGroups(entities.groups);
          setTeams(entities.teams);
          setSelectedTeam(entities.selected?.team);
          setSelectedGroup(entities.selected?.group);
          setSelectedAthlete(entities.selected?.athlete);
          setLoading(false);
        }
      );
      return (): void => entitiesSubscriber.unsubscribe();
    }, []);

    /**
     * @description handle change to selected entity
     * @param mEntityType
     * @param id
     */
    const onChange = (mEntityType: EntityType, id: string): void => {
      switch (mEntityType) {
        case 'athletes':
          setSelectedEntitySettings({
            ...settings,
            athleteId: id,
          });
          break;
        case 'groups':
          setSelectedEntitySettings({
            ...settings,
            groupId: id,
          });
          break;
        case 'teams':
          setSelectedEntitySettings({
            ...settings,
            teamId: id,
          });
          break;
      }
    };

    const renderEntity = (): React.ReactElement => {
      if (loading) return null;
      switch (props.match.params.entityType as EntityType) {
        case 'athletes':
          return <EntityView entity={selectedAthlete} loading={loading} />;
        case 'groups':
          return <EntityView entity={selectedGroup} loading={loading} />;
        case 'teams':
          return <EntityView entity={selectedTeam} loading={loading} />;
      }
    };

    const renderEntitySelector = (): React.ReactElement => {
      switch (props.match.params.entityType as EntityType) {
        case 'athletes':
          return (
            <EntitySelector
              selectedEntity='athletes'
              selectedId={settings?.athleteId}
              teams={teams}
              groups={groups}
              teamsLoading={loading}
              groupsLoading={loading}
              athletes={athletes}
              loading={loading}
              onChange={onChange}
              selectedTeamId={selectedTeam?.id}
              selectedGroupId={selectedGroup?.id}
              selectedAthleteId={selectedAthlete?.id}
            />
          );
        case 'groups':
          return (
            <EntitySelector
              selectedEntity='groups'
              selectedId={settings?.groupId}
              teamsLoading={loading}
              groupsLoading={loading}
              teams={teams}
              groups={groups}
              athletes={athletes}
              loading={loading}
              onChange={onChange}
              selectedTeamId={selectedTeam?.id}
              selectedGroupId={selectedGroup?.id}
              selectedAthleteId={selectedAthlete?.id}
            />
          );
        case 'teams':
          return (
            <EntitySelector
              selectedEntity='teams'
              selectedId={settings?.teamId}
              teams={teams}
              groups={groups}
              teamsLoading={loading}
              groupsLoading={loading}
              athletes={athletes}
              loading={loading}
              isAdmin={isAdmin}
              onChange={onChange}
              selectedTeamId={selectedTeam?.id}
              selectedGroupId={selectedGroup?.id}
              selectedAthleteId={selectedAthlete?.id}
            />
          );
      }
    };

    return (
      <MainGrid direction='horizontal'>
        {renderEntitySelector()}
        <RightSide>{renderEntity()}</RightSide>
      </MainGrid>
    );
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DashboardRow = styled(Row as any)`
  flex: 1;
  padding-top: var(--grid-gap);
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DashboardItem = styled(Card as any)`
  flex: 1;
  &:not(:last-child) {
    margin-right: var(--grid-gap);
  }
`;

export const Action = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RightSide = styled(Column as any)`
  max-height: calc(100vh - 70px);
  grid-column: 6/25;
  grid-row: 1/25;
  @media (max-width: 990px) {
    grid-column: 1 / 2;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 120px);
  }
`;
